/**
 * SpanishHeader — full ES navigation header that mirrors the EN Header structure
 * but with native Spanish labels and /es/* link targets.
 *
 * Rendered by Layout (App.js) when location.pathname.startsWith('/es').
 * Keeps the same desktop dropdown + mobile collapsible UX as the EN version
 * so the visual identity stays consistent across both languages.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import LangToggle from '@/components/LangToggle';
import { ROUTE_MAP_EN_TO_ES } from '@/i18n/routeMap';

const r = ROUTE_MAP_EN_TO_ES;

// Service categories with ES labels — slugs come from the route map so the
// LangToggle and SpanishHeader stay in sync. If a service exists in EN but
// not yet in the route map, it falls back to the EN slug under /es/.
const SERVICE_GROUPS = [
  [
    ['Injerto de Hueso', r['/bone-grafting']],
    ['Coronas y Puentes', r['/crowns-bridges']],
    ['Dentaduras Postizas', r['/dentures']],
    ['Enfermedad de las Encías', r['/gum-disease']],
    ['Odontología Láser', r['/laser-dentistry']],
    ['Cirugía Ósea', r['/osseous-surgery']],
    ['Odontología Preventiva', r['/preventative-dentistry']],
    ['Endodoncia', r['/root-canals']],
    ['Blanqueamiento Dental', r['/teeth-whitening']],
  ],
  [
    ['Bruxismo', r['/bruxism-teeth-grinding']],
    ['Implantes Dentales', r['/dental-implants']],
    ['Gingivectomía', r['/gingivectomy']],
    ['Invisalign®', r['/invisalign']],
    ['Ajuste de Mordida', r['/occlusal-bite-adjustment']],
    ['Carillas de Porcelana', r['/porcelain-veneers']],
    ['Aumento de Cresta', r['/ridge-augmentation']],
    ['Limpieza Profunda', r['/scaling-root-planing']],
  ],
  [
    ['Empastes', r['/composite-fillings']],
    ['⭐ Centro de Implantes', r['/implant-center'], true],
    ['Regeneración Tisular', r['/guided-tissue-bone-regeneration']],
    ['LANAP®', r['/lanap']],
    ['Detección de Cáncer Oral', r['/oral-cancer-screening']],
    ['⭐ First Fit Carillas', r['/first-fit-veneers'], true],
    ['Preservación de Cresta', r['/ridge-preservation']],
    ['Sedación Dental', r['/sedation']],
  ],
];

const SPECIALS = [
  ['Todas las Promociones', r['/specials'], 'header'],
  ['⭐ Centro de Implantes', r['/implant-center'], 'star'],
  ['Implantes — Consulta Gratis', r['/dental-implants-free-consult-with-x-rays']],
  ['Invisalign® — Consulta Gratis', r['/invisalign-free-consultation']],
  ['Limpieza + Examen $199', r['/179-healthy-cleaning-exam-and-set-of-x-rays']],
  ['Emergencia $99', r['/99-emergency-exam-and-x-ray-affordable-dental-services']],
  ['LANAP® — Consulta Gratis', r['/free-lanap-consultation']],
];

const SpanishHeader = ({ onBookAppointment }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState(null);
  const toggleMobileSubmenu = (k) => setMobileSubmenu(mobileSubmenu === k ? null : k);
  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <header className="bg-white shadow-sm sticky top-0 z-30" data-testid="spanish-header">
      {/* Top bar */}
      <div className="bg-teal-700 text-white">
        <div className="container mx-auto px-4 py-2">
          <div className="flex flex-wrap items-center justify-between">
            <div className="flex items-center space-x-4 text-sm">
              <span className="hidden sm:inline">📍 4620 Citylake Blvd W, Fort Worth, TX 76132</span>
            </div>
            <div className="flex items-center space-x-4 sm:space-x-6 mt-2 sm:mt-0">
              <a href="tel:8179853337" className="text-sm hover:text-teal-200">📞 (817) 985-3337</a>
              <span className="text-sm hidden sm:inline">¡Llámanos hoy!</span>
              <LangToggle />
            </div>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <Link to="/es" className="flex items-center bg-teal-700 px-4 py-2 rounded shadow-sm" data-testid="es-header-logo">
            <img src="/logo-fwd.png" alt="Fort Worth Dental" className="h-10 sm:h-12 w-auto" />
          </Link>

          <button
            className="xl:hidden p-2 text-gray-700 hover:text-teal-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Abrir menú"
            data-testid="es-mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Desktop nav — Spanish labels are longer than English (e.g.
              "Sobre Nosotros", "Formularios y Finanzas"), so we use the xl
              breakpoint (1280px) instead of lg (1024px) to give them room
              and prevent overlap with the logo. whitespace-nowrap keeps
              every label on a single line. */}
          <ul className="hidden xl:flex space-x-4 items-center ml-8 whitespace-nowrap">
            <li><Link to="/es" className="text-gray-700 hover:text-teal-600 font-medium">Inicio</Link></li>

            {/* Sobre Nosotros dropdown */}
            <li className="relative group">
              <button className="flex items-center text-gray-700 hover:text-teal-600 font-medium" aria-haspopup="true">
                Sobre Nosotros <ChevronDown className="ml-1 w-4 h-4" />
              </button>
              <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 absolute top-full left-0 mt-2 bg-white shadow-lg rounded-md py-2 w-56">
                <Link to={r['/about-us']} className="block px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600">Sobre Nosotros</Link>
                <Link to={r['/our-offices']} className="block px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600">Nuestras Oficinas</Link>
                <Link to={r['/our-team']} className="block px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600">Nuestro Equipo</Link>
                <Link to={r['/areas-we-serve']} className="block px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600">Áreas que Atendemos</Link>
                <Link to={r['/press-releases']} className="block px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600">Comunicados de Prensa</Link>
              </div>
            </li>

            {/* Servicios dropdown */}
            <li className="relative group">
              <button className="flex items-center text-gray-700 hover:text-teal-600 font-medium" aria-haspopup="true">
                Servicios <ChevronDown className="ml-1 w-4 h-4" />
              </button>
              <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 absolute top-full left-0 mt-2 bg-white shadow-lg rounded-md py-2 w-[680px]">
                <div className="flex">
                  {SERVICE_GROUPS.map((group, i) => (
                    <div key={i} className="flex-1">
                      {group.map(([label, path, isStar]) => (
                        <Link
                          key={path || label}
                          to={path || '/es/servicios'}
                          className={`block px-3 py-1 hover:bg-teal-50 hover:text-teal-600 text-sm leading-tight ${isStar ? 'text-teal-700 font-semibold' : 'text-gray-700'}`}
                        >
                          {label}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-100 mt-2 pt-2 px-3">
                  <Link to="/es/servicios" className="text-sm text-teal-600 font-semibold hover:underline">Ver todos los servicios →</Link>
                </div>
              </div>
            </li>

            {/* Promociones dropdown */}
            <li className="relative group">
              <button className="flex items-center text-gray-700 hover:text-teal-600 font-medium" aria-haspopup="true">
                Promociones <ChevronDown className="ml-1 w-4 h-4" />
              </button>
              <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 absolute top-full left-0 mt-2 bg-white shadow-lg rounded-md py-2 w-80">
                {SPECIALS.map(([label, path, kind]) => (
                  <Link
                    key={path}
                    to={path}
                    className={`block px-4 py-2 hover:bg-teal-50 hover:text-teal-600 text-sm ${
                      kind === 'header' ? 'font-semibold text-gray-700 border-b' :
                      kind === 'star' ? 'font-semibold text-teal-700' : 'text-gray-700'
                    }`}
                  >
                    {label}
                  </Link>
                ))}
              </div>
            </li>

            <li><Link to={r['/form-finances']} className="text-gray-700 hover:text-teal-600 font-medium">Formularios y Finanzas</Link></li>
            <li><Link to={r['/testimonials']} className="text-gray-700 hover:text-teal-600 font-medium">Testimonios</Link></li>
            <li><Link to="/es/blog" className="text-gray-700 hover:text-teal-600 font-medium">Noticias Dentales</Link></li>
            <li><Link to="/es/contacto" className="text-gray-700 hover:text-teal-600 font-medium">Contacto</Link></li>

            <li>
              <button
                onClick={onBookAppointment}
                className="bg-teal-500 text-white px-5 py-2 rounded-md hover:bg-teal-600 font-medium text-sm"
                data-testid="es-header-book-cta"
              >
                Reservar Cita
              </button>
            </li>
          </ul>
        </div>

        {/* Mobile nav */}
        {mobileMenuOpen && (
          <div className="xl:hidden mt-4 pb-4 border-t pt-4">
            <ul className="space-y-2">
              <li><Link to="/es" className="block py-2 text-gray-700 hover:text-teal-600 font-medium" onClick={closeMobile}>Inicio</Link></li>

              <li>
                <button
                  className="flex items-center justify-between w-full py-2 text-gray-700 hover:text-teal-600 font-medium"
                  onClick={() => toggleMobileSubmenu('about')}
                >
                  Sobre Nosotros <ChevronDown className={`w-4 h-4 transition-transform ${mobileSubmenu === 'about' ? 'rotate-180' : ''}`} />
                </button>
                {mobileSubmenu === 'about' && (
                  <div className="pl-4 space-y-2 mt-2">
                    <Link to={r['/about-us']} className="block py-1 text-gray-600 hover:text-teal-600" onClick={closeMobile}>Sobre Nosotros</Link>
                    <Link to={r['/our-offices']} className="block py-1 text-gray-600 hover:text-teal-600" onClick={closeMobile}>Nuestras Oficinas</Link>
                    <Link to={r['/our-team']} className="block py-1 text-gray-600 hover:text-teal-600" onClick={closeMobile}>Nuestro Equipo</Link>
                    <Link to={r['/areas-we-serve']} className="block py-1 text-gray-600 hover:text-teal-600" onClick={closeMobile}>Áreas que Atendemos</Link>
                  </div>
                )}
              </li>

              <li>
                <button
                  className="flex items-center justify-between w-full py-2 text-gray-700 hover:text-teal-600 font-medium"
                  onClick={() => toggleMobileSubmenu('services')}
                >
                  Servicios <ChevronDown className={`w-4 h-4 transition-transform ${mobileSubmenu === 'services' ? 'rotate-180' : ''}`} />
                </button>
                {mobileSubmenu === 'services' && (
                  <div className="pl-4 space-y-2 mt-2 max-h-60 overflow-y-auto">
                    <Link to="/es/servicios" className="block py-1 text-teal-600 hover:text-teal-700 font-semibold" onClick={closeMobile}>Ver todos →</Link>
                    <Link to={r['/dental-implants']} className="block py-1 text-gray-600 hover:text-teal-600" onClick={closeMobile}>Implantes Dentales</Link>
                    <Link to={r['/invisalign']} className="block py-1 text-gray-600 hover:text-teal-600" onClick={closeMobile}>Invisalign®</Link>
                    <Link to={r['/teeth-whitening']} className="block py-1 text-gray-600 hover:text-teal-600" onClick={closeMobile}>Blanqueamiento</Link>
                    <Link to={r['/cosmetic-dentistry']} className="block py-1 text-gray-600 hover:text-teal-600" onClick={closeMobile}>Odontología Cosmética</Link>
                    <Link to={r['/emergency-dentist']} className="block py-1 text-gray-600 hover:text-teal-600" onClick={closeMobile}>Emergencia</Link>
                    <Link to={r['/root-canals']} className="block py-1 text-gray-600 hover:text-teal-600" onClick={closeMobile}>Endodoncia</Link>
                  </div>
                )}
              </li>

              <li>
                <button
                  className="flex items-center justify-between w-full py-2 text-gray-700 hover:text-teal-600 font-medium"
                  onClick={() => toggleMobileSubmenu('specials')}
                >
                  Promociones <ChevronDown className={`w-4 h-4 transition-transform ${mobileSubmenu === 'specials' ? 'rotate-180' : ''}`} />
                </button>
                {mobileSubmenu === 'specials' && (
                  <div className="pl-4 space-y-2 mt-2">
                    <Link to={r['/specials']} className="block py-1 text-gray-600 hover:text-teal-600 font-semibold" onClick={closeMobile}>Todas las Promociones</Link>
                    <Link to={r['/dental-implants-free-consult-with-x-rays']} className="block py-1 text-gray-600 hover:text-teal-600" onClick={closeMobile}>Implantes Consulta Gratis</Link>
                    <Link to={r['/invisalign-free-consultation']} className="block py-1 text-gray-600 hover:text-teal-600" onClick={closeMobile}>Invisalign® Gratis</Link>
                    <Link to={r['/179-healthy-cleaning-exam-and-set-of-x-rays']} className="block py-1 text-gray-600 hover:text-teal-600" onClick={closeMobile}>Limpieza $199</Link>
                    <Link to={r['/99-emergency-exam-and-x-ray-affordable-dental-services']} className="block py-1 text-gray-600 hover:text-teal-600" onClick={closeMobile}>Emergencia $99</Link>
                  </div>
                )}
              </li>

              <li><Link to={r['/form-finances']} className="block py-2 text-gray-700 hover:text-teal-600 font-medium" onClick={closeMobile}>Formularios y Finanzas</Link></li>
              <li><Link to={r['/testimonials']} className="block py-2 text-gray-700 hover:text-teal-600 font-medium" onClick={closeMobile}>Testimonios</Link></li>
              <li><Link to="/es/blog" className="block py-2 text-gray-700 hover:text-teal-600 font-medium" onClick={closeMobile}>Noticias Dentales</Link></li>
              <li><Link to="/es/contacto" className="block py-2 text-gray-700 hover:text-teal-600 font-medium" onClick={closeMobile}>Contacto</Link></li>

              <li className="pt-2 border-t border-gray-100">
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-500">Idioma / Language</span>
                  <LangToggle compact className="text-teal-600 hover:text-teal-700" />
                </div>
              </li>

              <li className="pt-4">
                <button
                  onClick={() => { closeMobile(); onBookAppointment?.(); }}
                  className="w-full bg-teal-500 text-white px-5 py-2 rounded-md hover:bg-teal-600 font-medium"
                >
                  Reservar Cita
                </button>
              </li>
            </ul>
          </div>
        )}
      </nav>
    </header>
  );
};

export default SpanishHeader;
